import React, { useContext } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';

import UserContext from '../../../contexts/UserContext';
import Input from '../../../components/Input';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { INTERNAL_DRIVER_DROP_OFF_SCAN } from './query';

const DriverDropOffScanSchema = Yup.object().shape({
  comment: Yup.string().required('Du musst einen Kommentar eingeben'),
});

interface DriverDropOffScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
  trackingNumber: string;
  accessPointID: string;
}

const DriverDropOffScanModal: React.FC<DriverDropOffScanModalProps> = ({
  isOpen,
  onClose,
  refetch,
  trackingNumber,
  accessPointID,
}) => {
  const [internalDriverDropOffScan, { loading }] = useMutation(INTERNAL_DRIVER_DROP_OFF_SCAN);
  const { user } = useContext(UserContext);
  const toast = useCustomToast();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Driver Drop-Off Scan simulieren</ModalHeader>

        <Formik
          initialValues={{
            comment: '',
          }}
          validationSchema={DriverDropOffScanSchema}
          onSubmit={async ({ comment }) => {
            try {
              const result = await internalDriverDropOffScan({
                variables: {
                  trackingNumber,
                  accessPointID,
                  comment,
                  internalUserId: user.id,
                },
              });

              if (result.data?.internalDriverDropOffScan === 'success') {
                toast({
                  title: 'Erfolgreich',
                  description: 'Driver Drop-Off Scan wurde erfolgreich ausgelöst',
                  status: 'success',
                });
                await refetch();
                onClose();
              } else if (result.data?.internalDriverDropOffScan === 'not found') {
                toast({
                  title: 'Fehler',
                  description: 'Paket wurde nicht gefunden',
                  status: 'error',
                });
              } else {
                toast({
                  title: 'Fehler',
                  description: 'Der Scan konnte nicht durchgeführt werden. Das Paket wurde möglicherweise bereits gescannt.',
                  status: 'error',
                });
              }
            } catch (error: any) {
              toast({
                title: 'Fehler',
                description: error.message || 'Ein unbekannter Fehler ist aufgetreten',
                status: 'error',
              });
            }
          }}>
          {({ isSubmitting }) => (
            <Form>
              <ModalBody>
                <Text mb="4" fontSize="sm">
                  Du bist dabei, einen Driver Drop-Off Scan für das Paket <strong>{trackingNumber}</strong> zu
                  simulieren. Bitte gib einen Kommentar ein, warum du diese Aktion durchführst.
                </Text>
                <Input name="comment" label="Kommentar" placeholder="Grund für die Simulation..." />
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose} isDisabled={loading}>
                  Abbrechen
                </Button>
                <Button ml="4" size="sm" colorScheme="brand" type="submit" isLoading={loading || isSubmitting}>
                  Scan ausführen
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default DriverDropOffScanModal;
